import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { COLLECT_ROLES, FINANCE_ROLES, MONEY_LIMITS, RECEIPT_STATUSES, periodOf, type Fund, type RatePlanRow, type ReceiptRow } from '@mahallu/shared';
import { ApiError, MONEY_KEYS, loadErrorKey, money, useDuesMonth, useFunds, useHouses, useMe, useRatePlans, useReceipts } from '../api';
import { ConfirmButton, FormFooter, FormSection, Modal, SelectField, TextAreaField, TextField } from '../components/Form';
import { Icon } from '../components/Icon';
import { Badge, PageHeader, Stat } from '../components/Parts';
import { dateTime, inr, monthLabel } from '../format';
import { CollectForm } from './CollectForm';

const errorText = (e: unknown) => (e instanceof ApiError ? e.message : String(e));
const fundName = (f: { nameEn: string; nameMl: string | null }, lang: string) => (lang === 'ml' && f.nameMl ? f.nameMl : f.nameEn);

function useRole() {
  const me = useMe();
  const role = me.data?.role;
  return { canCollect: Boolean(role && COLLECT_ROLES.includes(role)), isFinance: Boolean(role && FINANCE_ROLES.includes(role)) };
}

/** Varisankhya › Dues & Arrears: one month at a time; the committee raises the month's dues once. */
export function Dues() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const { isFinance } = useRole();
  const now = periodOf(new Date());
  const [period, setPeriod] = useState(now);
  const month = useDuesMonth(period);
  const raise = useMutation({
    mutationFn: () => money.raiseDues(period),
    onSuccess: async () => Promise.all(MONEY_KEYS.map((queryKey) => qc.invalidateQueries({ queryKey }))),
  });
  const m = month.data;
  return (
    <>
      <PageHeader icon="wallet" title={t('dues.title')} desc={t('dues.desc')} />
      <div className="action-row filter-row">
        <TextField label={t('dues.month')} type="month" value={period} max={now} onChange={(v) => v && setPeriod(v)} />
        <span className="spacer" />
        <Link className="btn" to="/register/members?tile=arrears">
          {t('dues.arrearsList')}
        </Link>
      </div>
      {month.isError ? <p className="form-error">{t(loadErrorKey(month.error))}</p> : null}
      {m ? (
        <>
          <div className="strip">
            <Stat icon="home" tone="brand" value={`${m.raisedHouses} / ${m.eligibleHouses}`} label={t('dues.raisedHouses')} />
            <Stat icon="wallet" tone="teal" value={inr(m.amountInr)} label={t('dues.due', { month: monthLabel(period, i18n.language) })} />
            <Stat icon="check" tone="ok" value={inr(m.paidInr)} label={t('dues.paid')} />
            <Stat icon="bell" tone={m.amountInr - m.paidInr > 0 ? 'warn' : 'ok'} value={inr(m.amountInr - m.paidInr)} label={t('dues.unpaid')} />
          </div>
          {isFinance ? (
            <section className="card" style={{ marginTop: 16 }}>
              <div className="section-h">
                <h2>{t('dues.raiseTitle', { month: monthLabel(period, i18n.language) })}</h2>
              </div>
              <p className="note">{m.raisedHouses >= m.eligibleHouses ? t('dues.allRaised') : t('dues.raiseHint', { n: m.eligibleHouses - m.raisedHouses })}</p>
              <div className="action-row">
                <ConfirmButton label={t('dues.raise')} disabled={raise.isPending || m.raisedHouses >= m.eligibleHouses} onConfirm={() => raise.mutate()} />
                {raise.isSuccess ? <p className="note" role="status">{t('dues.raised', { n: raise.data.created, amount: inr(raise.data.amountInr) })}</p> : null}
                {raise.isError ? <p className="form-error" role="alert">{errorText(raise.error)}</p> : null}
              </div>
            </section>
          ) : null}
        </>
      ) : null}
    </>
  );
}

/** Varisankhya › Collect Payment: find the house, or record a donation from someone outside the register. */
export function Collect() {
  const { t } = useTranslation();
  const { canCollect } = useRole();
  const [q, setQ] = useState('');
  const [term, setTerm] = useState('');
  const [house, setHouse] = useState<{ id: string; houseNo: string; nameEn: string } | null | 'donation'>(null);
  useEffect(() => {
    const id = setTimeout(() => setTerm(q.trim()), 300);
    return () => clearTimeout(id);
  }, [q]);
  const list = useHouses({ q: term || undefined, status: 'ACTIVE', page: 1, size: 10 });
  return (
    <>
      <PageHeader
        icon="wallet"
        title={t('collect.pageTitle')}
        desc={t('collect.pageDesc')}
        actions={canCollect ? [{ label: t('collect.donation'), icon: 'plus', onClick: () => setHouse('donation') }] : []}
      />
      <div className="filter-row action-row">
        <TextField label={t('collect.find')} type="search" value={q} onChange={setQ} maxLength={100} />
      </div>
      {list.isError ? <p className="form-error">{t(loadErrorKey(list.error))}</p> : null}
      <div className="tbl-wrap">
        <div className="tbl-scroll">
          <table>
            <thead>
              <tr>
                <th>{t('col.house')}</th>
                <th>{t('col.head')}</th>
                <th>{t('col.plan')}</th>
                <th>{t('col.arrears')}</th>
                {canCollect ? <th>{t('col.actions')}</th> : null}
              </tr>
            </thead>
            <tbody>
              {(list.data?.rows ?? []).map((h) => (
                <tr key={h.id}>
                  <td>
                    <b>{h.nameEn}</b> <span className="note num">{h.houseNo}</span>
                  </td>
                  <td>{h.headName ?? '—'}</td>
                  <td className="num">{inr(h.monthlyRateInr)}</td>
                  <td className="num">{h.arrearsInr ? inr(h.arrearsInr) : '—'}</td>
                  {canCollect ? (
                    <td>
                      <button type="button" className="btn sm" onClick={() => setHouse({ id: h.id, houseNo: h.houseNo, nameEn: h.nameEn })}>
                        {t('collect.title')}
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {house ? <CollectForm house={house === 'donation' ? null : house} onClose={() => setHouse(null)} /> : null}
    </>
  );
}

function CancelForm({ receipt, onClose }: { receipt: ReceiptRow; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const save = useMutation({
    mutationFn: () => money.cancelReceipt(receipt.id, reason.trim(), receipt.version),
    onSuccess: async () => {
      await Promise.all(MONEY_KEYS.map((queryKey) => qc.invalidateQueries({ queryKey })));
      onClose();
    },
  });
  return (
    <Modal
      title={`${t('rcpt.cancel')} · ${receipt.receiptNo}`}
      onClose={onClose}
      footer={<FormFooter onCancel={onClose} saving={save.isPending} saveLabel={t('rcpt.cancel')} error={save.isError ? errorText(save.error) : undefined} />}
    >
      <form
        id="active-form"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <p className="callout small">{t('rcpt.cancelHint', { amount: inr(receipt.amountInr) })}</p>
        <FormSection title={t('rcpt.cancel')}>
          <TextAreaField label={t('rcpt.reason')} value={reason} onChange={setReason} maxLength={MONEY_LIMITS.reason} rows={3} required full />
        </FormSection>
      </form>
    </Modal>
  );
}

/** Varisankhya › Receipts: every receipt, cancelled ones included; totals count only issued receipts. */
export function Receipts() {
  const { t, i18n } = useTranslation();
  const { isFinance } = useRole();
  const funds = useFunds();
  const [f, setF] = useState({ fund: '', status: '', from: '', to: '', q: '', page: 1 });
  const list = useReceipts({ ...f, size: 25 });
  const [cancelling, setCancelling] = useState<ReceiptRow | null>(null);
  const set = (k: keyof typeof f) => (v: string) => setF((p) => ({ ...p, [k]: v, page: 1 }));
  const d = list.data;
  const pages = d ? Math.max(1, Math.ceil(d.total / d.size)) : 1;
  return (
    <>
      <PageHeader icon="file" title={t('rcpt.title')} desc={t('rcpt.desc')} />
      <div className="filters receipt-filters">
        <SelectField label={t('collect.fund')} value={f.fund} onChange={set('fund')} empty={t('rcpt.allFunds')} options={(funds.data ?? []).map((x) => ({ value: x.code, label: fundName(x, i18n.language) }))} />
        <SelectField label={t('rcpt.status')} value={f.status} onChange={set('status')} empty={t('rcpt.allStatus')} options={RECEIPT_STATUSES.map((s) => ({ value: s, label: t(`rcpt.${s}`) }))} />
        <TextField label={t('rcpt.from')} type="date" value={f.from} onChange={set('from')} />
        <TextField label={t('rcpt.to')} type="date" value={f.to} onChange={set('to')} />
        <TextField label={t('rcpt.search')} type="search" value={f.q} onChange={set('q')} maxLength={100} />
      </div>
      {d ? <p className="note">{t('rcpt.totals', { n: d.total, amount: inr(d.issuedInr) })}</p> : null}
      {list.isError ? <p className="form-error">{t(loadErrorKey(list.error))}</p> : null}
      <div className="tbl-wrap">
        <div className="tbl-scroll">
          <table>
            <thead>
              <tr>
                <th>{t('rcpt.no')}</th>
                <th>{t('rcpt.date')}</th>
                <th>{t('rcpt.payer')}</th>
                <th>{t('collect.fund')}</th>
                <th>{t('collect.amount')}</th>
                <th>{t('collect.method')}</th>
                <th>{t('rcpt.by')}</th>
                <th>{t('rcpt.status')}</th>
                <th>{t('col.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {(d?.rows ?? []).map((r) => (
                <tr key={r.id} className={r.status === 'CANCELLED' ? 'muted-row' : undefined}>
                  <td className="num">{r.receiptNo}</td>
                  <td className="num">{r.receivedOn}</td>
                  <td>
                    {r.house ? (
                      <>
                        {r.house.nameEn} <span className="note num">{r.house.houseNo}</span>
                      </>
                    ) : (
                      r.payerName
                    )}
                    {r.periods.length ? <div className="note">{r.periods.map((p) => monthLabel(p, i18n.language)).join(', ')}</div> : null}
                  </td>
                  <td>{fundName(r.fund, i18n.language)}</td>
                  <td className="num">{inr(r.amountInr)}</td>
                  <td>{t(`method.${r.method}`)}</td>
                  <td>
                    {r.collectedBy}
                    <div className="note">{dateTime(r.createdAt, i18n.language)}</div>
                  </td>
                  <td>
                    <Badge tone={r.status === 'ISSUED' ? 'ok' : 'neutral'}>{t(`rcpt.${r.status}`)}</Badge>
                    {r.cancelReason ? <div className="note">{r.cancelReason}</div> : null}
                    {r.notice ? <div className="note">{t('notice.short', { channel: r.notice.channel ? t(`channel.${r.notice.channel}`) : t('channel.pending'), status: t(`mstatus2.${r.notice.status}`) })}</div> : null}
                  </td>
                  <td className="row-actions">
                    <Link className="icon-btn" to={`/receipts/${r.id}/print`} target="_blank" rel="noopener" title={t('collect.print')} aria-label={`${t('collect.print')} ${r.receiptNo}`}>
                      <Icon name="file" />
                    </Link>
                    {isFinance && r.status === 'ISSUED' ? (
                      <button type="button" className="btn sm" onClick={() => setCancelling(r)}>
                        {t('rcpt.cancel')}
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
              {d && !d.rows.length ? (
                <tr>
                  <td colSpan={9} className="note">
                    {t('rcpt.none')}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
      {pages > 1 ? (
        <div className="action-row">
          <button type="button" className="btn sm" disabled={f.page <= 1} onClick={() => setF((p) => ({ ...p, page: p.page - 1 }))}>
            {t('rcpt.prev')}
          </button>
          <span className="note">{t('rcpt.page', { page: f.page, pages })}</span>
          <button type="button" className="btn sm" disabled={f.page >= pages} onClick={() => setF((p) => ({ ...p, page: p.page + 1 }))}>
            {t('rcpt.next')}
          </button>
        </div>
      ) : null}
      {cancelling ? <CancelForm receipt={cancelling} onClose={() => setCancelling(null)} /> : null}
    </>
  );
}

function PlanRow({ p, canEdit }: { p: RatePlanRow; canEdit: boolean }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [amount, setAmount] = useState<number | ''>(p.monthlyInr);
  const [apply, setApply] = useState(true);
  const save = useMutation({
    mutationFn: () => money.updatePlan(p.code, { monthlyInr: Number(amount), applyToHouses: apply, version: p.version }),
    onSuccess: async (rows) => {
      qc.setQueryData(['money', 'plans'], rows);
      await qc.invalidateQueries({ queryKey: ['houses'] });
    },
  });
  return (
    <tr>
      <td>
        <b>{i18n.language === 'ml' && p.nameMl ? p.nameMl : p.nameEn}</b> <span className="note">{t(`plan.${p.code}`)}</span>
      </td>
      <td className="num">{p.houses}</td>
      <td>
        {canEdit && p.code !== 'EXEMPT' ? (
          <form
            className="inline-months"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate();
            }}
          >
            <input type="number" min={0} max={MONEY_LIMITS.monthly} value={amount} aria-label={`${t('plans.monthly')}: ${p.nameEn}`} onChange={(e) => setAmount(e.target.value === '' ? '' : Number(e.target.value))} />
            <label className="check">
              <input type="checkbox" checked={apply} onChange={(e) => setApply(e.target.checked)} />
              {t('plans.apply')}
            </label>
            <button type="submit" className="btn sm" disabled={save.isPending || amount === '' || amount === p.monthlyInr}>
              {t('seq.set')}
            </button>
            {save.isError ? <span className="form-error">{errorText(save.error)}</span> : null}
          </form>
        ) : (
          <span className="num">{inr(p.monthlyInr)}</span>
        )}
      </td>
    </tr>
  );
}

function FundForm({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [nameMl, setNameMl] = useState('');
  const save = useMutation({
    mutationFn: () => money.createFund({ code: code.trim(), nameEn: nameEn.trim(), nameMl: nameMl.trim() || null }),
    onSuccess: (rows) => {
      qc.setQueryData(['money', 'funds'], rows);
      onClose();
    },
  });
  return (
    <Modal title={t('funds.add')} onClose={onClose} footer={<FormFooter onCancel={onClose} saving={save.isPending} saveLabel={t('funds.add')} error={save.isError ? errorText(save.error) : undefined} />}>
      <form
        id="active-form"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <FormSection title={t('funds.section')}>
          <TextField label={t('funds.code')} value={code} onChange={(v) => setCode(v.toUpperCase().replace(/[^A-Z0-9_]/g, ''))} maxLength={30} required hint={t('funds.codeHint')} />
          <TextField label={t('wards.nameEn')} value={nameEn} onChange={setNameEn} maxLength={80} required />
          <TextField label={t('wards.nameMl')} value={nameMl} onChange={setNameMl} maxLength={80} lang="ml" full />
        </FormSection>
      </form>
    </Modal>
  );
}

/** Varisankhya › Rate Plans: the monthly amount per plan, and the funds money can be given to. */
export function PlansFunds() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const { isFinance } = useRole();
  const plans = useRatePlans();
  const funds = useFunds();
  const [adding, setAdding] = useState(false);
  const toggle = useMutation({
    mutationFn: (f: Fund) => money.updateFund(f.code, { active: !f.active, version: f.version }),
    onSuccess: (rows) => qc.setQueryData(['money', 'funds'], rows),
  });
  return (
    <>
      <PageHeader icon="wallet" title={t('plans.title')} desc={t('plans.desc')} actions={isFinance ? [{ label: t('funds.add'), icon: 'plus', onClick: () => setAdding(true) }] : []} />
      {plans.isError ? <p className="form-error">{t(loadErrorKey(plans.error))}</p> : null}
      <div className="privacy-grid">
        <section className="card">
          <div className="section-h">
            <h2>{t('plans.plans')}</h2>
          </div>
          <p className="note">{t('plans.hint')}</p>
          <div className="tbl-wrap">
            <div className="tbl-scroll">
              <table>
                <thead>
                  <tr>
                    <th>{t('col.plan')}</th>
                    <th>{t('plans.houses')}</th>
                    <th>{t('plans.monthly')}</th>
                  </tr>
                </thead>
                <tbody>{(plans.data ?? []).map((p) => <PlanRow key={`${p.code}-${p.version}`} p={p} canEdit={isFinance} />)}</tbody>
              </table>
            </div>
          </div>
        </section>
        <section className="card">
          <div className="section-h">
            <h2>{t('funds.title')}</h2>
          </div>
          <p className="note">{t('funds.hint')}</p>
          <ul className="history">
            {(funds.data ?? []).map((f) => (
              <li key={f.code} className="fund-row">
                <b className={i18n.language === 'ml' && f.nameMl ? 'ml' : undefined}>{fundName(f, i18n.language)}</b> <span className="note num">{f.code}</span>{' '}
                <Badge tone={f.active ? 'ok' : 'neutral'}>{f.active ? t('funds.open') : t('funds.closed')}</Badge>
                {f.kind === 'DUES' ? <span className="note"> · {t('funds.dues')}</span> : null}
                <span className="spacer" />
                {isFinance && f.kind !== 'DUES' ? (
                  <button type="button" className="btn sm" disabled={toggle.isPending} onClick={() => toggle.mutate(f)}>
                    {f.active ? t('funds.close') : t('funds.reopen')}
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
          {toggle.isError ? <p className="form-error">{errorText(toggle.error)}</p> : null}
        </section>
      </div>
      {adding ? <FundForm onClose={() => setAdding(false)} /> : null}
    </>
  );
}

